import React, { useContext } from "react"
import { useController } from "react-hook-form"

import classNames from "classnames"

import { useMergedRefs } from "shared/lib/useMergedRefs"
import { Ref } from "shared/types/Ref"
import { InputControlWrapper } from "shared/ui/input/InputControlWrapper"

import { Input as DefaultInput, InputContext } from "../../Input"
import { useInputDescriptionIDs } from "../../accessibility/useInputAriaDescription"
import { ReadonlySelect } from "../components/ReadonlySelect"
import { SelectValue } from "../components/SelectValue"
import { BoxSelectControl, BoxSelectProps } from "./BoxSelectControl"
import { BoxSelectLoader } from "./BoxSelectLoader"

const Root = React.memo(
  React.forwardRef(
    (
      {
        className,
        ...props
      }: React.ComponentPropsWithoutRef<typeof DefaultInput.Root>,
      ref?: React.ForwardedRef<HTMLDivElement>
    ) => {
      return (
        <DefaultInput.Root
          {...props}
          className={classNames("box-select-input", className)}
          ref={ref}
        />
      )
    }
  )
)

function InputBase<T, M extends boolean>({
  className,
  inputRef,
  ...props
}: BoxSelectProps<T, M>) {
  const { inputRef: contextInputRef } = useContext(InputContext)
  const { disabled } = useContext(InputContext)
  const { ariaDescribedBy } = useInputDescriptionIDs({
    additionalDescriptions: props.ariaDescribedBy
  })

  const resolvedClassName = classNames("input__control", className)

  const mergedRefs = useMergedRefs(
    inputRef,
    contextInputRef as Ref<HTMLButtonElement>
  )

  return (
    <InputControlWrapper
      Loader={<BoxSelectLoader className={resolvedClassName} />}
    >
      <BoxSelectControl
        {...props}
        inputRef={mergedRefs}
        isDisabled={disabled}
        className={resolvedClassName}
        ariaDescribedBy={ariaDescribedBy}
      />
    </InputControlWrapper>
  )
}

function HookFormInput<T, M extends boolean>({
  inputRef,
  ...props
}: BoxSelectProps<T, M>) {
  const { name, required } = useContext(InputContext)
  const {
    field: { ref, ...field }
  } = useController({
    name,
    rules: { required, value: props.value }
  })

  const mergedRefs = useMergedRefs(ref, inputRef)

  return <InputBase {...field} {...props} inputRef={mergedRefs} />
}

function Input<T, M extends boolean>(props: BoxSelectProps<T, M>) {
  const { name } = useContext(InputContext)
  if (name) {
    return <HookFormInput {...props} />
  }

  return <InputBase {...props} />
}

const BoxSelect = {
  ...DefaultInput,
  Root,
  Readonly: ReadonlySelect,
  Value: SelectValue,
  Input
}
export { BoxSelect }
